import { markIntentionalFlowPause } from "@/utils/sentry/intentional";
import { SHORT_MESSAGE } from "@/api/shortMessages";
import { requestTokenizeCard } from "@/api/m4u/tokenizeCard";
import { RequestValue } from "@/api/options";
import { ProductFetch } from "@/api/product";
import { GetSaleFetch } from "@/api/sale/getSale";
import { offerExpress } from "@/api/sale/offerExpress";
import { requestActivateSale } from "@/api/sale/activateSale";
import { requestInitM4UAcquisition } from "@/api/sale/initM4UAcquisition";
import { requestSaveTokenizedCard } from "@/api/sale/saveTokenizedCard";
import { sendStepUserJourney } from "@/api/userJourney/userJourney";
import Button from "@/components/Button/Button";
import Column from "@/components/Column/Column";
import Columns from "@/components/Columns";
import CreditCardExpress, {
  creditCardExpressSchema,
} from "@/components/CreditCardExpress";
import VerifiedCustomerBanner from "@/components/VerifiedCustomerBanner";
import ErrorText from "@/components/ErrorText";
import Footer from "@/components/Footer";
import Form from "@/components/Form";
import ProductsSidebar from "@/components/ProductsSidebar";
import SEO from "@/components/SEO";
import TopBar from "@/components/TopBar";
import UnauthorizedAlert from "@/components/UnauthorizedAlert";
import usePageSubmit from "@/hooks/usePageSubmit";
import { useErrorStore } from "@/store/error";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import { formatPrice } from "@/utils/currency";
import { formatCPF } from "@/utils/string";
import { GenericObject } from "@/utils/types";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const NEXT_ROUTE = "/confirmation/auto-debt";
const UNAUTHORIZED_TRANSACTION = SHORT_MESSAGE.UNAUTHORIZED_TRANSACTION;

export const OfertaExpress = () => {
  const [searchParams] = useSearchParams();
  const identifier =
    searchParams?.get("cpf") ?? searchParams?.get("msisdn") ?? searchParams?.get("key") ?? "";

  const {
    data: sale,
    isLoading,
    error,
  } = GetSaleFetch(searchParams?.get("venda_id") ?? "", identifier ?? "");
  const { data: product, isLoading: isLoadingProduct } = ProductFetch(
    sale?.plano_id ?? 0,
  );
  const setProduct = useProductStore((store) => store.setProduct);
  const setPayment = useProductStore((store) => store.setPayment);
  const [formPaymentIsValid, setFormPaymentIsValid] = useState(false);
  const [clientNumber, setClientNumber] = useState("");
  const navigate = useNavigate();
  const [submitButtonText, setSubmitButtonText] = useState("");
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const addData = useFormStore((store) => store.addData);
  const setSaleId = useFormStore((store) => store.setSaleId);
  const saleId = useFormStore((store) => store.saleId);
  const [saleError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showUnauthorizedAlert, setShowUnauthorizedAlert] = useState(false);
  const showModalError = useErrorStore((store) => store.showModal);
  const setMessageError = useErrorStore((store) => store.setMessage);

  useEffect(() => {
    if (isLoadingSubmit) {
      setSubmitButtonText("Carregando ...");
    } else if (!formPaymentIsValid) {
      setSubmitButtonText("Preencha os campos para continuar");
    } else {
      setSubmitButtonText("Finalizar Pedido");
    }
  }, [isLoadingSubmit, formPaymentIsValid]);

  useEffect(() => {
    if (!isLoading) {
      if (error) {
        setMessageError(
          "Não foi possível carregar os dados da venda. Tente novamente.",
        );
        showModalError();
        navigate("/");
        return;
      }

      if (sale) {
        const number = sale.tipo === "NOVA_LINHA" ? sale.telefone : sale.msisdn;
        setClientNumber(number);

        if (sale.jornada_uuid) {
          sendStepUserJourney({
            uuid: sale.jornada_uuid,
            passo: "OFERTA_EXPRESS_EXIBIDA",
          });
        }

        if (!isLoadingProduct) {
          if (!product) {
            setMessageError(
              "Não foi possível carregar o plano. Tente novamente.",
            );
            showModalError();
            navigate("/");
            return;
          }

          const price = product.precos?.[0]?.preco_atual;
          if (!price) {
            setMessageError(
              "Não foi possível carregar o preço do plano. Tente novamente.",
            );
            showModalError();
            navigate("/");
            return;
          }

          setProduct(product);
          setPayment({
            value: "Cartão de Crédito",
            label: "Cartão de Crédito",
            type: `R$${formatPrice(price)}`,
          } as RequestValue);

          if (sale.segmento === "EXPRESS") {
            navigate("/personal-data");
            return;
          }

          if (sale.status !== "NOVO" && sale.status !== "CANCELADO") {
            setMessageError(
              "Modalidade de pagamento via cartão de crédito não está disponível para este pedido.",
            );
            showModalError();
            navigate("/");
            return;
          }
        }
      }
    }
  }, [
    error,
    isLoading,
    isLoadingProduct,
    product,
    navigate,
    sale,
    setMessageError,
    setPayment,
    setProduct,
    showModalError,
  ]);

  const handleSubmitError = (err: {
    response?: { data?: { shortMessage?: string } };
  }) => {
    const message = err.response?.data?.shortMessage;

    setIsLoadingSubmit(false);
    setError(true);

    if (message === UNAUTHORIZED_TRANSACTION) {
      setErrorMessage(message);
      setShowUnauthorizedAlert(true);
    } else {
      setErrorMessage(
        "Ocorreu um erro ao processar o pagamento. Tente novamente.",
      );
    }

    // Erro real ja foi capturado pelo interceptor do axios; throw aqui eh
    // apenas para "pausar" o fluxo apos exibir mensagem/alert ao usuario.
    throw markIntentionalFlowPause(err);
  };

  const handleSubmit = async (newData: GenericObject) => {
    try {
      if (sale) {
        setIsLoadingSubmit(true);
        let expressSaleID = 0;

        if (!saleId) {
          expressSaleID = await offerExpress(sale.id, sale.plano_id, {
            utm_campaign: searchParams?.get("utm_campaign") ?? "",
            utm_medium: searchParams?.get("utm_medium") ?? "",
            utm_source: searchParams?.get("utm_source") ?? "",
          });

          setSaleId(expressSaleID);
        }

        if (sale.jornada_uuid) {
          sendStepUserJourney({
            uuid: sale.jornada_uuid,
            passo: "OFERTA_EXPRESS_ACEITA",
          });
        }

        const token = await requestTokenizeCard({
          cartao_numero: newData.payment.creditCard.replace(/\D/g, ""),
          cartao_mes: newData.payment.dueMonth,
          cartao_ano: newData.payment.dueYear,
        });

        if (sale.jornada_uuid) {
          sendStepUserJourney({
            uuid: sale.jornada_uuid,
            passo: "CARTAO_TOKENIZADO",
          });
        }

        await requestSaveTokenizedCard({
          venda_id: saleId ?? expressSaleID,
          cliente_cpf: sale.cpf,
          cartao_tokenizado: token.token,
          cartao_cvv: newData.payment.securityCode,
        });

        try {
          if (sale.tipo === "MIGRACAO") {
            await requestActivateSale({
              venda_id: saleId ?? expressSaleID,
              cliente_cpf: sale.cpf,
            });
          } else {
            await requestInitM4UAcquisition({
              venda_id: saleId ?? expressSaleID,
              cliente_cpf: sale.cpf,
            });
          }

          if (sale.jornada_uuid) {
            sendStepUserJourney({
              uuid: sale.jornada_uuid,
              passo: "CONCLUIR_VENDA_EXPRESS",
            });
          }
        } catch (activationErr: unknown) {
          const shortMessage = (activationErr as { response?: { data?: { shortMessage?: string } } })
            ?.response?.data?.shortMessage;

          if (shortMessage === UNAUTHORIZED_TRANSACTION) {
            setIsLoadingSubmit(false);
            setError(true);
            setErrorMessage(shortMessage);
            setShowUnauthorizedAlert(true);
            return;
          }
          // Erro não-UNAUTHORIZED: segue para tela de sucesso,
          // mas NÃO registra o passo — ativação não ocorreu em tempo real.
        }

        addData({
          fullName: sale.nome,
          cpf: formatCPF(sale.cpf),
          email: sale.email,
        });

        setIsLoadingSubmit(false);
      }
    } catch (e: unknown) {
      handleSubmitError(
        e as { response?: { data?: { shortMessage?: string } } },
      );
    }
  };

  const handleOnSubmit = usePageSubmit(NEXT_ROUTE, handleSubmit);

  if (isLoading || isLoadingProduct || !sale) {
    return (
      <div className="min-w-screen flex min-h-screen items-center justify-center">
        <img
          src="/tim_logo.svg"
          alt="Logo da empresa TIM"
          className="h-24 animate-pulse"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6f9]">
      <SEO title="Oferta Express" />
      {showUnauthorizedAlert && (
        <UnauthorizedAlert
          setShowUnauthorizedAlert={setShowUnauthorizedAlert}
        />
      )}
      <TopBar />

      <div className="mx-auto max-w-7xl px-2 py-6 lg:px-6">
        <Columns
          as={Form}
          onSubmit={handleOnSubmit}
          validationSchema={creditCardExpressSchema}
          className="flex flex-row-reverse justify-center print:shadow-none"
          useNegativeMargin={false}
        >
          <Column size={[12, 12, 3]}>
            <ProductsSidebar />
          </Column>
          <Column size={[12, 12, 7]} className="mt-4 lg:mt-0">
            <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
              <div className="px-4 pb-10 pt-6 md:px-12">
                <div className="text-tim-primary">
                  <p>Olá, {sale.nome.split(" ")[0]}!</p>
                  <p>
                    Confira seus dados e seu endereço que já temos salvos em nossos
                    sistemas e continue pagando com cartão de crédito.
                  </p>
                </div>

                <VerifiedCustomerBanner
                  nome={sale.nome}
                  cpf={sale.cpf}
                  email={sale.email}
                  telefone={clientNumber}
                  logradouro={sale.logradouro}
                  numero={sale.numero}
                  bairro={sale.bairro}
                  cep={sale.cep}
                  complemento={sale.complemento}
                />
                <div className="pb-4 text-tim-primary">
                  <p>
                    Utilize seu cartão de crédito para preencher os campos abaixo.
                  </p>
                </div>

                <CreditCardExpress setFormIsValid={setFormPaymentIsValid} />

                <Column size={12} className="pb-6 text-center">
                  {saleError ? (
                    <ErrorText id="process-error">
                      {errorMessage === null
                        ? "Ocorreu um erro ao finalizar o processo. Por favor, tente novamente."
                        : errorMessage}
                    </ErrorText>
                  ) : null}
                </Column>

                <Column size={12}>
                  <Button
                    type="submit"
                    large
                    disabled={!formPaymentIsValid}
                    helperText={
                      !formPaymentIsValid ? "todos os campos são obrigatórios" : ""
                    }
                    loading={isLoading || isLoadingSubmit}
                  >
                    {submitButtonText}
                  </Button>
                </Column>
              </div>
            </div>
          </Column>
        </Columns>
        <Column className="mb-4 mt-8">
          <Footer showText />
        </Column>
      </div>
    </div>
  );
};

export default OfertaExpress;
